import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from 'components/Box'
import Text from 'components/Text'
import VirtualizedList from 'components/VirtualizedList'

import { KeyCode } from 'utils/constants/keyCode'

import { GroupedOption, Option } from './Option'
import { MenuListContainer, StyledItem } from './styles'
import { MenuListProps, PopOverItem } from './types'
import { colors } from 'utils/colors'

const findValueIndex = (options?: PopOverItem[], selected?: PopOverItem | PopOverItem[]) => {
  let index = -1

  if (!options || !selected) {
    return index
  }

  if (Array.isArray(selected)) {
    selected.forEach((s) => {
      if (index < 0) {
        index = options.findIndex((o) => o.value === s.value)
      }
    })

    return index
  }

  return options.findIndex((o) => o.value === selected.value)
}

const scrollToFocused = (list: HTMLUListElement | null, item: HTMLLIElement | null) => {
  if (!list || !item) {
    return
  }

  const buffer = item.offsetHeight * 3

  // Check and scroll upwards if focused item top position exceeds the list's top position
  if (item.offsetTop - item.offsetHeight + buffer < list.offsetTop) {
    list.scrollTop = item.offsetTop - item.offsetHeight + buffer
    return
  }

  // Check and scroll downwards if focused item bottom position exceeds list's bottom position
  const listBottomPosition = list.offsetTop + list.offsetHeight
  const itemBottomPosition = item.offsetTop + item.offsetHeight

  if (itemBottomPosition + buffer > listBottomPosition) {
    list.scrollTop = item.offsetTop - list.offsetHeight + item.offsetHeight
  }
}

const MenuList = ({ isVirtualized, ...props }: MenuListProps) => {
  const { t } = useTranslation()
  const listRef = useRef<HTMLUListElement>(null)
  const focusedRef = useRef<HTMLLIElement>(null)
  const [hasKeyPressed, setHasKeyPressed] = useState(false)

  const [focusedIndex, setFocusedIndex] = useState(findValueIndex(props.options, props.value))

  const OptionComponent = props.isGrouped ? GroupedOption : Option

  const indexMapped = useMemo(
    () =>
      props.options?.reduce(
        (acc, curr) => {
          let currIdx = acc.lastIndex
          if (curr.options && curr.options.length > 0) {
            const subList = []
            // parent
            subList.push({ value: curr.value, index: currIdx })
            currIdx++
            // sub options
            curr.options.forEach((subOption) => {
              subList.push({ value: subOption.value, index: currIdx })
              currIdx++
            })
            return {
              list: [...acc.list, ...subList],
              lastIndex: currIdx + 1,
            }
          }
          return {
            list: [...acc.list, { value: curr.value, index: currIdx }],
            lastIndex: currIdx + 1,
          }
        },
        {
          list: [] as { value: string | number; index: number }[],
          lastIndex: 0,
        },
      ) ?? {
        list: [],
        lastIndex: 0,
      },
    [props.options],
  )

  // Effect to select currently focused option
  useEffect(() => {
    const selectFocused = (e: KeyboardEvent) => {
      setHasKeyPressed(true)

      if (!props.options || focusedIndex < 0) {
        return
      }

      if (e.key === KeyCode.enter) {
        props.onSelect(props.options[focusedIndex])
      }
    }

    addEventListener('keydown', selectFocused)

    return () => {
      removeEventListener('keydown', selectFocused)
    }
  }, [focusedIndex, props])

  // Effect to shift focus to previous/next option
  useEffect(() => {
    const moveFocus = (e: KeyboardEvent) => {
      if (!indexMapped.list) {
        return
      }

      if (e.key === KeyCode.arrowUp) {
        setFocusedIndex((focusIndex) => (focusIndex > 0 ? focusIndex - 1 : 0))
        return
      }

      if (e.key === KeyCode.arrowDown) {
        const lastIndex = indexMapped.lastIndex - 1

        setFocusedIndex((focusIndex) => (focusIndex < lastIndex ? focusIndex + 1 : lastIndex))
        return
      }
    }

    addEventListener('keydown', moveFocus)

    return () => {
      removeEventListener('keydown', moveFocus)
    }
  }, [indexMapped])

  const showHighlightFocus = hasKeyPressed && focusedIndex >= 0

  // Scroll on keyboard navigate
  showHighlightFocus && scrollToFocused(listRef.current, focusedRef.current)

  useEffect(() => {
    // Initial scroll to focus
    !isVirtualized && scrollToFocused(listRef.current, focusedRef.current)
  }, [showHighlightFocus, isVirtualized])

  if (isVirtualized) {
    return (
      <MenuListContainer isSearchable={props.isSearchable} ref={listRef}>
        {props.isSelectedOnTop && Array.isArray(props.value) && Boolean(props.value.length) && (
          <Box display="flex" justifyContent="space-between" paddingY={1} paddingX={2}>
            <Text color="gray5">
              {t('selected_count', {
                count: props.value.length,
              })}
            </Text>
            <Text
              color="gray5"
              textDecoration="underline"
              {...(props?.onReset && {
                onClick: props.onReset,
              })}
            >
              reset_lbl
            </Text>
          </Box>
        )}
        <VirtualizedList
          height="200px"
          width="300px"
          rowCount={props.options?.length ?? 0}
          rowGetter={(index: number) => {
            if (!props.options?.[index]) return <></>

            const option = props.options[index]
            const isFocused = indexMapped.list.find((i) => i.value === option.value)?.index === focusedIndex

            return (
              <OptionComponent
                key={index}
                menuProps={props}
                optionProps={{
                  ...props.options?.[index],
                  isFocused: showHighlightFocus ? isFocused : false,
                }}
                ref={isFocused ? focusedRef : undefined}
              />
            )
          }}
          initialScrollIndex={findValueIndex(props.options, props.value)}
        />
      </MenuListContainer>
    )
  }

  return (
    <MenuListContainer isSearchable={props.isSearchable} ref={listRef}>
      {props.isSelectedOnTop && Array.isArray(props.value) && Boolean(props.value.length) && (
        <Box display="flex" justifyContent="space-between" paddingY={1} paddingX={2}>
          <Text color="gray5">
            {t('selected_count', {
              count: props.value.length,
            })}
          </Text>
          <Text
            color="gray5"
            textDecoration="underline"
            {...(props?.onReset && {
              onClick: props.onReset,
            })}
          >
            reset_lbl
          </Text>
        </Box>
      )}
      {props.isSelectedOnTop && Array.isArray(props.value) && props.value.length > 0 && (
        <Box style={props.isDense ? undefined : { borderBottom: `1px solid ${colors.gray3}` }}>
          {props.value.map((i, index) => {
            const isFocused = indexMapped.list.find((li) => li.value === i.value)?.index === focusedIndex

            return (
              <Box key={index} onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                <OptionComponent
                  menuProps={props}
                  optionProps={{
                    ...i,
                    isFocused: showHighlightFocus ? isFocused : false,
                  }}
                  ref={isFocused ? focusedRef : undefined}
                />
              </Box>
            )
          })}
        </Box>
      )}

      {(props.options ?? []).map((i, index) => {
        const isFocused = indexMapped.list.find((li) => li.value === i.value)?.index === focusedIndex
        const isChecked = Array.isArray(props.value)
          ? props.value.map((v) => v.value).includes(i.value)
          : props.value?.value === i.value

        return (
          <StyledItem
            key={index}
            isSubmenu={props.isSubmenu}
            isDense={props.isDense || props.isSelectedOnTop}
            top={props.top}
            borderBottom={props.isDense ? 'none' : `1px solid ${colors.gray3}`}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            <OptionComponent
              menuProps={props}
              optionProps={{
                ...i,
                isFocused: showHighlightFocus ? isFocused : false,
              }}
              ref={isFocused ? focusedRef : undefined}
            />
            {isChecked && i.options?.length && i.options.length > 0 && (
              <Box borderTop={`1px solid ${colors.gray3}`}>
                {i.options.map((subOption, subIndex) => {
                  const isFocused = indexMapped.list.find((li) => li.value === subOption.value)?.index === focusedIndex

                  return (
                    <OptionComponent
                      key={subIndex}
                      menuProps={props}
                      optionProps={{
                        isIndented: true,
                        ...subOption,
                        isFocused: showHighlightFocus ? isFocused : false,
                      }}
                      ref={isFocused ? focusedRef : undefined}
                    />
                  )
                })}
              </Box>
            )}
          </StyledItem>
        )
      })}
    </MenuListContainer>
  )
}

export default MenuList
